
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { trans } from '../../../_providers/Translation'
import { Table, TableBody, TableCell, TableContainer, TableHead, Paper, TextField, Select, MenuItem, InputAdornment, Avatar, LinearProgress, Typography, Box, IconButton, Tooltip } from '@mui/material'
import NoDataFound from '../../partials/NoDataFound'
import Api from '../../../_helpers/Api'
import DateRange from '@/components/partials/DateRange'
import Skeleton from 'react-loading-skeleton'
import Dropdown from '@/components/partials/Dropdown'


let timeout = null
function BonusPoliciesDashboard({
    update
}) {
    const [dateRange, setDateRange] = React.useState({ start: null, end: null })
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchFilter, setSearchFilter] = useState('')
    const [policyFilter, setPolicyFilter] = useState(null)
    const [employeeFilter, setEmployeeFilter] = useState(null)
    const [filters, setFilters] = useState(false)
    const [filterPolicies, setFilterPolicies] = useState([])
    const [policyTypeId, setPolicyTypeId] = useState(null)

    const employeesDropdownRef = useRef(null)


    // Fetch all periods when filters change
    useEffect(() => {
        setLoading(true)
        // Build params from filters
        const params = {}
        if (dateRange.start) params.date_from = dateRange.start
        if (dateRange.end) params.date_to = dateRange.end
        if (searchFilter) params.search = searchFilter
        if (policyFilter) params.bonus_policy_id = policyFilter
        if (employeeFilter) params.employee_id = employeeFilter

        Api.get('partners/bonuspolicies/dashboard', { params })
            .then(res => {
                setRows(res.data.bonus_policy.technicians)
                setPolicyTypeId(res.data.bonus_policy.type_id ?? res.data.bonus_policy.policy?.type_id ?? null)
                setFilterPolicies(res.data.filter_options.policies)
                if (!policyFilter) {
                    setPolicyFilter(res.data.filter_options.policies.length > 0 ? res.data.filter_options.policies[0].id : null)
                }
            })
            .finally(() => setLoading(false))
    }, [dateRange.start, dateRange.end, update, filters, policyFilter, employeeFilter])


    const handleStartDateChange = date => setDateRange(prev => ({ ...prev, start: date }))
    const handleEndDateChange = date => setDateRange(prev => ({ ...prev, end: date }))

    // Handler to clear all filters
    const handleClearFilters = () => {
        setDateRange({ start: null, end: null })
        setSearchFilter('')
        setFilters(new Date().getTime())
        setEmployeeFilter(null)
        setPolicyFilter(filterPolicies.length > 0 ? filterPolicies[0].id : null)
        employeesDropdownRef.current.reset()
    }

    function handleSearch(e) {
        clearTimeout(timeout)
        setSearchFilter(e.target.value)

        timeout = setTimeout(() => {
            setFilters(new Date().getTime())
        }, 300)

    }

    const selectedPolicy = filterPolicies.find(policy => String(policy.id) === String(policyFilter))
    const isType3 = Number(policyTypeId ?? selectedPolicy?.type_id) === 3

    return (
        <>
            <h3>{trans('tabs.dashboard')}</h3>
            <div className='filter-standard'>
                <div className='col'>
                    <input
                        id="search"
                        type="search"
                        name="name"
                        value={searchFilter}
                        onChange={handleSearch}
                        placeholder={trans('labels.search')}
                    />
                </div>
                {filterPolicies.length ?
                    <div className='col'>
                        <select
                            value={policyFilter}
                            onChange={e => setPolicyFilter(e.target.value)}
                            style={{
                                minWidth: "200px"
                            }}
                        >
                            {filterPolicies.map(policy => (
                                <option key={policy.id} value={policy.id}>{policy.name}</option>
                            ))}
                        </select>
                    </div>
                    :
                    <>
                    </>
                }
                <div className='col'>
                    <Dropdown
                        url="partners/employees/all?active=1&sort=name"
                        renderText={(data) => {
                            return `${data.name} ${data.lastname}`
                        }}
                        onChange={(data) => {
                            setEmployeeFilter(data.id)
                        }}
                        inputIdName="employee_id"
                        inputIdValue={filters.employee_id}
                        inputPlaceholder={trans("labels.user")}
                        ref={employeesDropdownRef}
                    />
                </div>
                <div className='col'>
                    <DateRange
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                        onStartDateChange={(date) => {
                            handleStartDateChange(date)
                        }} onEndDateChange={(date) => {
                            handleEndDateChange(date)
                        }}
                    />
                </div>
                <div className='col'>
                    <button className='button-clear-filter' onClick={handleClearFilters}>
                        {trans('buttons.clearFilter')}
                    </button>
                </div>
            </div>
            <hr />

            {loading ? (
                <Skeleton height={45} count={5} />
            ) : rows.length === 0 ? (
                <NoDataFound />
            ) : (
                <table className='unclickable'>
                    <thead>
                        <tr>
                            <th>{trans('labels.user')}</th>
                            <th className="center">{trans('labels.reservations')}</th>
                            {!isType3 &&
                                <>
                                    <th className="center">{trans('labels.delays')}</th>
                                    <th className="center">{trans('labels.reclamations')}</th>
                                </>
                            }
                            <th>{trans('labels.progress')}</th>
                            <th>{trans('labels.completionForecast')}</th>
                            <th className="right">{trans('labels.bonus')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <tr
                                key={row.employee_id || idx}
                                style={row.completed_target ? { backgroundColor: '#e6f9ed' } : {}}
                            >
                                <td>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Avatar style={{ width: 28, height: 28, marginRight: 8 }} />
                                        <span>{row.employee_name}</span>
                                    </Box>
                                </td>
                                <td className="center">{row.total_reservations}</td>
                                {!isType3 &&
                                    <>
                                        <td className="center">{row.not_on_time_count}</td>
                                        <td className="center">{row.reclamations_count}</td>
                                    </>
                                }
                                <td style={{ minWidth: 200 }}>
                                    <Tooltip followCursor title={
                                        row.bonus_policy_type === "reservations" ?
                                            <>
                                                {trans('random.completedReservations')}: {row.total_reservations || 0}
                                                <hr style={{ margin: '4px 0' }} />
                                                {trans('random.targetReservations')}: {row.target_reservations || 0}
                                            </>
                                            :
                                            row.bonus_policy_type === "created-reservations" ?
                                                <>
                                                    {trans('random.createdReservations')}: {row.total_reservations || 0}
                                                    <hr style={{ margin: '4px 0' }} />
                                                    {trans('random.targetReservations')}: {row.target_reservations || 0}
                                                </>
                                                :
                                                <>
                                                    {trans('random.pointsFromClients')}: {row.points_from_clients || 0}
                                                    <br />
                                                    {trans('random.pointsFromStatuses')}: {row.points_from_reservations || 0}
                                                    <hr style={{ margin: '4px 0' }} />
                                                    {trans('random.targetPoints')}: {row.target_points || 0}
                                                </>
                                    }>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Box width="100%" mr={1}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={row.progress_percentage}
                                                    sx={{
                                                        height: 12,
                                                        borderRadius: 20,
                                                        background: '#E7ECFF',
                                                        '& .MuiLinearProgress-bar': {
                                                            background: 'linear-gradient(90deg, #96D6BF 0%, #35B084 100%)',
                                                            borderRadius: 20
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                </td>
                                <td>
                                    <b>
                                        {row.estimated_completion_date ?? '-'}
                                    </b>
                                </td>
                                <td className="right">
                                    <b style={{ color: row.completed_target ? '#43b97f' : undefined, fontWeight: 600 }}>{`${row.total_earned_bonuses || '0.00'} ${row.currency?.name || 'EUR'}`}</b>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )
            }
        </>
    )
}

export default BonusPoliciesDashboard
